/**
 * Bulk Video Generation Modal
 * 
 * PART 1 & 4: Allows users to create multiple videos from content packs or saved items.
 * Shows progress during generation and final results.
 */

import React, { useState, useEffect, useRef } from 'react';
import { X, VideoCamera, Spinner, CheckCircle, XCircle, CreditCard, Lightning, Package } from '@phosphor-icons/react';
import { motion, AnimatePresence } from 'framer-motion';
import { videoAPI } from '@/utils/api';
import { toast } from 'sonner';

const BulkVideoModal = ({ 
  isOpen, 
  onClose, 
  contentItems = [], // Array of content items with id, title, script, video_prompt
  sourceType = 'content_pack',
  packTitle = 'Content Pack',
  videoCredits = 0,
  onRefreshCredits
}) => {
  const [stage, setStage] = useState('confirm'); // confirm, processing, complete
  const [batchId, setBatchId] = useState(null);
  const [progress, setProgress] = useState({ total: 0, completed: 0, failed: 0, current: 0 });
  const [jobs, setJobs] = useState([]);
  const [error, setError] = useState(null);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [creditsShort, setCreditsShort] = useState(0);
  // Phase E (2026-05-27): batch-level upcharge confirmation. If the v2
  // backend rejects with 402 because at least one extended-tier item
  // requires confirmation, we surface the prompt and re-submit with
  // `upcharge_confirmed: true`.
  const [upchargePrompt, setUpchargePrompt] = useState(null);
  const pollIntervalRef = useRef(null);

  const totalVideos = contentItems.length;
  const hasEnoughCredits = videoCredits >= totalVideos;

  useEffect(() => {
    if (isOpen) {
      setStage('confirm');
      setBatchId(null);
      setProgress({ total: totalVideos, completed: 0, failed: 0, current: 0 });
      setJobs([]);
      setError(null);
      setUpchargePrompt(null);
    }
    
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [isOpen, totalVideos]);

  const handleStartBulkGeneration = async (opts = {}) => {
    // PART 2: Credit check
    if (!hasEnoughCredits) {
      setCreditsShort(totalVideos - videoCredits);
      setShowPurchaseModal(true);
      return;
    }

    setStage('processing');
    setError(null);
    setUpchargePrompt(null);

    try {
      const contentIds = contentItems.map(item => item.id);
      // Phase E (2026-05-27): batch-level upcharge confirmation is
      // forwarded to bulk-create-v2 so extended-tier items in the
      // batch can proceed past the 402 gate.
      const response = await videoAPI.createBulkVideos(contentIds, sourceType, {
        upcharge_confirmed: Boolean(opts.upcharge_confirmed),
      });

      const { batch_id, total_videos } = response.data;
      setBatchId(batch_id);
      setProgress(prev => ({ ...prev, total: total_videos }));

      toast.success(`Started generating ${total_videos} videos`);

      // Start polling for status
      pollIntervalRef.current = setInterval(async () => {
        try {
          const statusResponse = await videoAPI.getBulkVideoStatus(batch_id);
          const { status, completed, failed, credits_used, jobs: jobList } = statusResponse.data;

          setProgress(prev => ({
            ...prev,
            completed,
            failed,
            current: completed + failed
          }));
          setJobs(jobList || []);

          if (status === 'completed' || status === 'partial_success' || status === 'failed') {
            clearInterval(pollIntervalRef.current);
            setStage('complete');

            if (onRefreshCredits) {
              onRefreshCredits();
            }

            if (status === 'completed') {
              toast.success(`All ${completed} videos created successfully!`);
            } else if (status === 'partial_success') {
              toast.warning(`${completed} videos completed, ${failed} failed`);
            } else {
              toast.error('Video generation failed');
            }
          }
        } catch (err) {
          console.error('Error polling bulk status:', err);
        }
      }, 5000); // Poll every 5 seconds

      // Timeout after 30 minutes
      setTimeout(() => {
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          if (stage === 'processing') {
            setStage('complete');
            setError('Generation timed out. Check My Creations for any completed videos.');
          }
        }
      }, 30 * 60 * 1000);

    } catch (err) {
      console.error('Bulk video error:', err);
      const errorDetail = err.response?.data?.detail;
      const status = err.response?.status;

      // Phase E (2026-05-27): bulk-create-v2 returns 402 with
      // `error: "upcharge_confirmation_required"` when at least one
      // item in the batch is extended-tier and the caller did not
      // confirm the upcharge. Surface a confirmation prompt and let
      // the user re-submit with `upcharge_confirmed: true`.
      if (
        status === 402 &&
        errorDetail &&
        typeof errorDetail === 'object' &&
        errorDetail.error === 'upcharge_confirmation_required'
      ) {
        setUpchargePrompt({
          item_index: errorDetail.item_index,
          content_id: errorDetail.content_id,
          upcharge_per_video_usd:
            errorDetail.upcharge_per_video_usd ?? errorDetail?.estimate?.upcharge_per_video_usd,
          message:
            errorDetail.message ||
            'One or more items in this batch require an extended-video upcharge.',
        });
        setStage('confirm');
        return;
      }

      if (errorDetail?.purchase_required) {
        setCreditsShort(errorDetail.credits_short || (totalVideos - videoCredits));
        setShowPurchaseModal(true);
        setStage('confirm');
      } else {
        // Round 56 — backend may return `detail` as an object
        // ({message, error, ...}). Passing that object to setError
        // would crash render (objects are not valid React children),
        // which is what produced the blank page. Coerce to a string.
        const errorString =
          typeof errorDetail === 'string'
            ? errorDetail
            : errorDetail && typeof errorDetail === 'object'
            ? (errorDetail.message || errorDetail.error || 'Failed to start bulk video generation')
            : 'Failed to start bulk video generation';
        setError(errorString);
        setStage('confirm');
      }
    }
  };

  const handleClose = () => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="bulk-video-modal">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-gray-900 rounded-2xl max-w-lg w-full paygbot-modal-shell border border-gray-800"
        >
          {/* Header */}
          <div className="p-6 border-b border-gray-800">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
                  <Package size={20} className="text-white" weight="fill" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">Create Videos for Pack</h2>
                  <p className="text-sm text-gray-400">{packTitle}</p>
                </div>
              </div>
              <button 
                onClick={handleClose}
                className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
                data-testid="close-bulk-modal"
              >
                <X size={20} className="text-gray-400" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            {/* Confirm Stage */}
            {stage === 'confirm' && (
              <div className="space-y-6">
                {/* Summary */}
                <div className="bg-gray-800/50 rounded-xl p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-400">Videos to create</span>
                    <span className="text-white font-bold text-lg">{totalVideos}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-400">Your credits</span>
                    <span className={`font-bold text-lg ${hasEnoughCredits ? 'text-green-400' : 'text-red-400'}`}>
                      {videoCredits}
                    </span>
                  </div>
                  <div className="flex items-center justify-between border-t border-gray-700 pt-3">
                    <span className="text-gray-400">Credits needed</span>
                    <span className="text-white font-bold text-lg">{totalVideos}</span>
                  </div>
                </div>

                {/* Warning if not enough credits */}
                {!hasEnoughCredits && (
                  <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
                    <div className="flex items-start gap-3">
                      <CreditCard size={20} className="text-red-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-red-400 font-medium">
                          You need {totalVideos - videoCredits} more video credits
                        </p>
                        <p className="text-red-400/70 text-sm mt-1">
                          Purchase credits to create all {totalVideos} videos
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Error message */}
                {error && (
                  <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
                    <p className="text-red-400 text-sm">{error}</p>
                  </div>
                )}

                {/* Phase E (2026-05-27) — extended-tier upcharge confirmation */}
                {upchargePrompt && (
                  <div
                    className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 space-y-3"
                    data-testid="bulk-upcharge-prompt"
                  >
                    <div className="flex items-start gap-3">
                      <Lightning size={20} className="text-amber-400 flex-shrink-0 mt-0.5" weight="fill" />
                      <div className="space-y-1">
                        <p className="text-amber-300 font-medium">
                          Extended video upcharge required
                        </p>
                        <p className="text-amber-200/80 text-sm">
                          {upchargePrompt.message}
                          {upchargePrompt.upcharge_per_video_usd
                            ? ` (+$${Number(upchargePrompt.upcharge_per_video_usd).toFixed(2)} per extended video).`
                            : null}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleStartBulkGeneration({ upcharge_confirmed: true })}
                      className="w-full py-2 px-3 bg-amber-500 hover:bg-amber-600 text-black font-semibold rounded-lg transition-colors text-sm"
                      data-testid="confirm-bulk-upcharge-btn"
                    >
                      Confirm upcharge & continue
                    </button>
                  </div>
                )}

                {/* Content items preview */}
                <div className="space-y-2">
                  <p className="text-sm text-gray-400 font-medium">Content items:</p>
                  <div className="max-h-40 overflow-y-auto space-y-2">
                    {contentItems.slice(0, 5).map((item, idx) => (
                      <div key={item.id} className="flex items-center gap-2 text-sm">
                        <VideoCamera size={14} className="text-purple-400" />
                        <span className="text-white truncate">{item.title || `Post ${idx + 1}`}</span>
                      </div>
                    ))}
                    {contentItems.length > 5 && (
                      <p className="text-gray-500 text-sm">+{contentItems.length - 5} more</p>
                    )}
                  </div>
                </div>

                {/* Action buttons */}
                <div className="flex gap-3">
                  <button
                    onClick={handleClose}
                    className="flex-1 py-3 px-4 bg-gray-800 hover:bg-gray-700 text-white font-medium rounded-xl transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleStartBulkGeneration()}
                    className="flex-1 py-3 px-4 bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-white font-semibold rounded-xl transition-all flex items-center justify-center gap-2"
                    data-testid="start-bulk-btn"
                  >
                    {hasEnoughCredits ? (
                      <>
                        <Lightning size={18} weight="fill" />
                        Create {totalVideos} Videos
                      </>
                    ) : (
                      <>
                        <CreditCard size={18} />
                        Buy Credits
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* Processing Stage - PART 4 */}
            {stage === 'processing' && (
              <div className="space-y-6">
                {/* Progress indicator */}
                <div className="text-center py-8">
                  <div className="w-16 h-16 mx-auto mb-4 relative">
                    <Spinner size={64} className="text-purple-500 animate-spin" />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-white font-bold text-lg">
                        {progress.current}
                      </span>
                    </div>
                  </div>
                  <p className="text-xl font-bold text-white mb-2">
                    Creating {progress.current + 1} of {progress.total}
                  </p>
                  <p className="text-gray-400 text-sm">
                    This may take a few minutes per video
                  </p>
                </div>

                {/* Progress bar */}
                <div className="relative h-3 bg-gray-800 rounded-full overflow-hidden">
                  <motion.div
                    className="absolute inset-y-0 left-0 bg-gradient-to-r from-violet-500 to-purple-600"
                    initial={{ width: 0 }}
                    animate={{ width: `${(progress.current / progress.total) * 100}%` }}
                    transition={{ duration: 0.5 }}
                  />
                </div>

                {/* Status counts */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-white">{progress.total}</p>
                    <p className="text-gray-500 text-xs">Total</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-green-400">{progress.completed}</p>
                    <p className="text-gray-500 text-xs">Completed</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-red-400">{progress.failed}</p>
                    <p className="text-gray-500 text-xs">Failed</p>
                  </div>
                </div>

                {/* Job list */}
                {jobs.length > 0 && (
                  <div className="space-y-2 max-h-32 overflow-y-auto">
                    {jobs.map((job, idx) => (
                      <div key={job.job_id} className="flex items-center gap-2 text-sm">
                        {job.status === 'completed' && <CheckCircle size={14} className="text-green-400" weight="fill" />}
                        {job.status === 'failed' && <XCircle size={14} className="text-red-400" weight="fill" />}
                        {job.status === 'processing' && <Spinner size={14} className="text-purple-400 animate-spin" />}
                        <span className={`${job.status === 'failed' ? 'text-red-400' : 'text-white'} truncate`}>
                          {job.title || `Video ${idx + 1}`}
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                <p className="text-center text-gray-500 text-xs">
                  You can close this modal - videos will continue generating
                </p>
              </div>
            )}

            {/* Complete Stage - PART 4 */}
            {stage === 'complete' && (
              <div className="space-y-6">
                {/* Result summary */}
                <div className="text-center py-6">
                  {progress.failed === 0 ? (
                    <CheckCircle size={64} className="mx-auto text-green-400 mb-4" weight="fill" />
                  ) : progress.completed > 0 ? (
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-yellow-500/20 flex items-center justify-center">
                      <span className="text-2xl">⚠️</span>
                    </div>
                  ) : (
                    <XCircle size={64} className="mx-auto text-red-400 mb-4" weight="fill" />
                  )}
                  
                  <h3 className="text-xl font-bold text-white mb-2">
                    {progress.failed === 0 ? 'All Videos Created!' : 
                     progress.completed > 0 ? 'Partially Complete' : 'Generation Failed'}
                  </h3>
                </div>

                {/* Final stats */}
                <div className="bg-gray-800/50 rounded-xl p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-400">Completed</span>
                    <span className="text-green-400 font-bold">{progress.completed}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-400">Failed</span>
                    <span className="text-red-400 font-bold">{progress.failed}</span>
                  </div>
                  <div className="flex items-center justify-between border-t border-gray-700 pt-3">
                    <span className="text-gray-400">Credits used</span>
                    <span className="text-white font-bold">{progress.completed}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-400">Credits remaining</span>
                    <span className="text-purple-400 font-bold">{videoCredits - progress.completed}</span>
                  </div>
                </div>

                {/* Job results */}
                {jobs.length > 0 && (
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {jobs.map((job, idx) => (
                      <div key={job.job_id} className="flex items-center justify-between bg-gray-800/30 rounded-lg p-2">
                        <div className="flex items-center gap-2">
                          {job.status === 'completed' && <CheckCircle size={14} className="text-green-400" weight="fill" />}
                          {job.status === 'failed' && <XCircle size={14} className="text-red-400" weight="fill" />}
                          <span className={`text-sm ${job.status === 'failed' ? 'text-red-400' : 'text-white'} truncate max-w-[200px]`}>
                            {job.title || `Video ${idx + 1}`}
                          </span>
                        </div>
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          job.status === 'completed' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                        }`}>
                          {job.status === 'completed' ? 'Ready' : 'Failed'}
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Action button */}
                <button
                  onClick={handleClose}
                  className="w-full py-3 px-4 bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-white font-semibold rounded-xl transition-all"
                  data-testid="done-bulk-btn"
                >
                  Done
                </button>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </>
  );
};

export default BulkVideoModal;
